"use client";
import React, { Suspense, useEffect, useState } from "react";
import { Skeleton } from "antd/lib";
import { useTranslations } from "next-intl";
import { useAuth } from "@/context/AuthContext";

// import SymbolPrediction from "@/app/components/products/SymbolPrediction";
import ProductMarket from "@/app/components/products/Market";

import TopNews from "@/app/components/TopNews";
import MyCard from "@/app/components/MyCard";
import LinkToKlardaApp from "@/app/components/LinkToKlardaApp";
import { CommentTimeline } from "@/app/components/products/Discussion";
import useAxiosQuery from "@/hooks/useMyQuery";
import type { IProductDetail } from "@/models/product";
import { usePrice } from "@/context/PriceContext";
import dynamic from "next/dynamic";
import ProductHeader from "@/app/components/products/Header";
import classNames from "classnames";
import ProductProfile from "@/app/components/products/ProductProfile";
import ProductSignals from "@/app/components/products/ProductSignals";
import ProductNews from "@/app/components/products/ProductNews";
import ProductForecast from "@/app/components/products/ProductForecast";
import { useSearchParams } from "next/navigation";

const TokenHealth = dynamic(() => import("@/app/components/products/TokenHealth"));
const PeopleAlsoWatch = dynamic(() => import("@/app/components/products/PeopleAlsoWatch"));
const InformationDetails = dynamic(() => import("@/app/components/products/InforDetails"));
const ProductSignal = dynamic(() => import("@/app/components/products/Singal"));
const ProductChart = dynamic(() => import("@/app/components/products/ProductChart"));
const ProductMarketOverview = dynamic(() => import("@/app/components/products/ProductMarket"));

const OverviewContent = ({ isLoading, selectedToken, response, params }: any) => {
  const t = useTranslations("home");

  return (
    <>
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-8">
          <Suspense fallback={<Skeleton active />}>
            <ProductChart height={500} isLoading={isLoading} selectedToken={selectedToken} isHideAds />
          </Suspense>
        </div>
        <div className="col-span-12 lg:col-span-4">
          <Suspense fallback={<Skeleton active />}>
            <TokenHealth isLoading={isLoading} productDetail={response?.data} />
          </Suspense>
        </div>
      </div>
      <InformationDetails productDetail={response?.data} isLoading={isLoading} />
      {/* <SymbolPrediction symbol={params.symbol} isLoading={isLoading} /> */}
      {/* <AdUnit slot="8889970778" style={{ display: "block", textAlign: "center" }} /> */}
      <PeopleAlsoWatch symbol={params.symbol} />
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12">
          <ProductMarket symbol={params.symbol} />
        </div>
        {/*<div className="col-span-12 lg:col-span-4">*/}
        {/*  <ProductSignal symbol={params.symbol} />*/}
        {/*</div>*/}
      </div>
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-8">
          <TopNews title={params.symbol + " " + t("new").toUpperCase()} subTitle={t("Top_news")} />
        </div>
        <div className="col-span-12 lg:col-span-4 flex flex-col justify-between gap-2">
          <MyCard
            styles={{ header: { textTransform: "uppercase" } }}
            title={params.symbol + " " + t("discussions")}
            extra={<LinkToKlardaApp />}
          >
            <CommentTimeline symbol={params.symbol} likeCallback={() => null} />
          </MyCard>
          {/* <AdUnit slot="3745455304" /> */}
        </div>
      </div>
    </>
  );
};

const tabs = [
  { name: "Overview", key: "Overview" },
  { name: "Profile", key: "Profile" },
  { name: "Markets", key: "Markets" },
  { name: "News", key: "News" },
  // { name: "Signals", key: "Signals" },
  // { name: "Forecast", key: "Forecast" },
];

const MarketDetail = React.memo(({ params }: { params: { symbol: string } }) => {
  const { authState } = useAuth();
  const { getToken } = usePrice();
  const searchParams = useSearchParams();
  const urlTab = searchParams.get("tab");

  const selectedToken = getToken(params.symbol);
  const { data: response, isLoading } = useAxiosQuery<{ data: IProductDetail }>({
    url: `v1/admin/token/details-by-symbol?symbol=${params.symbol}`,
    method: "get",
  });

  const [activeTab, setActiveTab] = useState("Overview");

  useEffect(() => {
    if (authState === "logged" && urlTab && tabs.some(tab => tab.key === urlTab)) {
      setActiveTab(urlTab);
    }
  }, [authState, urlTab]);

  const renderContent = () => {
    switch (activeTab) {
      case "Overview":
        return (
          <OverviewContent isLoading={isLoading} selectedToken={selectedToken} response={response} params={params} />
        );
      case "Profile":
        return <ProductProfile symbol={params.symbol} />;
      case "News":
        return <ProductNews symbol={params.symbol} />;
      case "Signals":
        return <ProductSignals symbol={params.symbol} />;
      case "Forecast":
        return <ProductForecast symbol={params.symbol} />;
      case "Markets":
        return (
          <Suspense fallback={<Skeleton active />}>
            <ProductMarketOverview symbol={params.symbol} />
          </Suspense>
        );
      default:
        return null;
    }
  };

  return (
    <main className="min-h-screen">
      <div className="flex flex-col gap-4">
        <ProductHeader activeTab={activeTab} symbol={params.symbol} productDetail={response?.data} />
        <div className="flex items-center space-x-2 sm:space-x-4 overflow-x-auto scrollbar-hide">
          {tabs.map(tab => (
            <button
              key={tab.key}
              className={classNames(
                "flex gap-1 items-center px-2 py-1  focus:outline-none font-normal whitespace-nowrap",
                {
                  "text-primary border-b-2 border-pritext-primary": activeTab === tab.key,
                },
              )}
              onClick={() => {
                setActiveTab(tab.key);
              }}
            >
              {tab.name}
            </button>
          ))}
        </div>
        <div className="mt-6">{renderContent()}</div>
      </div>
    </main>
  );
});

MarketDetail.displayName = "MarketDetail";
export default MarketDetail;
